import React from 'react';

import type { AdminInquiryDetail } from '@models/BookingInquiry';
import type { DeliveryInstruction } from '@models/DeliveryInstruction';
import type { InvoiceDetail } from '@models/Invoice';
import type { Invoice, InvoiceBase } from '@models/Invoice2';
import type { ArrivalNotice } from '@models/next/ArrivalNotice';
import type { CurrencyRate } from '@models/next/CurrencyRate';
import type { CustomsInstruction } from '@models/next/CustomsInstruction';
import type { CustomsRequest, CustomsRequestForm } from '@models/next/CustomsRequest';
import type { DeliveryRequestListItem, DeliveryRequestForm } from '@models/next/DeliveryRequest';
import type { InvoiceDetails } from '@models/next/Invoice';
import type {
  Payee,
  PaymentSlipDetailsMeta,
  PaymentSlipDetailsForm,
  PaymentSlipInfoForm,
  PaymentSlip,
} from '@models/next/PaymentSlip';
import type { PreAlertDetails } from '@models/next/PreAlert';
import type { SeaWaybill } from '@models/next/SeaWaybill';
import type { BaseUser, User } from '@models/User';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

export interface BookingFieldContextValues {
  mode: 'edit' | 'display';
  onEdit?: (name: string, label: string) => void;
}

export const BookingFieldContext = React.createContext<BookingFieldContextValues>({ mode: 'edit' });

export interface ArrivalNoticeContextValues {
  bookingInquiry: AdminInquiryDetail;
  preAlert: PreAlertDetails;
  arrivalNotice: ArrivalNotice;
  isPaymentSlipCreated: boolean;
}

export const ArrivalNoticeContext = React.createContext<ArrivalNoticeContextValues>({} as any);

export interface SeaWaybillContextValues {
  seaWaybill: SeaWaybill;
}

export const SeaWaybillContext = React.createContext<SeaWaybillContextValues>({} as any);

export const invoiceDetailsSearchType = t.type({
  selectedInvoiceDetailIds: h.omittable(t.array(h.string())),
  selectedInvoiceIds: h.omittable(t.array(h.string())),
});

export interface InvoiceFormDialogContextValues {
  type: 'create' | 'update';
  onChangeType: (type: 'create' | 'update') => void;
  inputInvoice?: Invoice; // The remote data to edit
  onChangeInputInvoice: (invoice: Invoice) => void;
  invoiceBase: InvoiceBase; // The current form data
  onChangeInvoiceBase: (v: InvoiceBase) => void;
  user: User;
  bookingInquiry: AdminInquiryDetail;
  invoiceDetails: InvoiceDetail[];
  invoiceIdList: number[];
  onChangeInvoiceIdList: (ids: number[]) => void;
  step: number;
  onChangeStep: (step: number) => void;
  data?: Invoice; // The remote data
  onChangeData: (data: Invoice | undefined) => void;
  error?: Error;
  onChangeError: (error: Error | undefined) => void;
  onClose: () => void;
}

export const InvoiceFormDialogContext = React.createContext<InvoiceFormDialogContextValues>(
  {} as any,
);

export interface BookingDetailsContextValues {
  preAlert: PreAlertDetails;
  bookingInquiry: AdminInquiryDetail;
  user: BaseUser;
}

export const BookingDetailsContext = React.createContext<BookingDetailsContextValues>(
  undefined as any,
);

export interface CustomsRequestFormDialogContextValuesBase {
  type: 'create' | 'update';
  open: boolean;
  onClose: () => void;
  d2dNo: string;
  customsInstruction: CustomsInstruction;
  customsRequest?: CustomsRequest;
  onSuccess?: (
    action: 'createCustomsRequest' | 'submitCustomsRequest' | 'urlSubmitCustomsRequest',
  ) => void;
}

export interface CustomsRequestFormDialogContextValues
  extends CustomsRequestFormDialogContextValuesBase {
  initialValues?: CustomsRequestForm;
  bookingInquiry?: AdminInquiryDetail;
  arrivalNotice?: ArrivalNotice;
  preAlert?: PreAlertDetails;
}

export const CustomsRequestFormDialogContext =
  React.createContext<CustomsRequestFormDialogContextValues>({} as any);

export interface DeliveryRequestFormDialogContextValuesBase {
  type: 'create' | 'update';
  open: boolean;
  onClose: () => void;
  d2dNo: string;
  deliveryInstruction: DeliveryInstruction;
  setDeliveryInstruction?: (v: DeliveryInstruction) => void;
  deliveryRequest?: DeliveryRequestListItem;
}

export interface DeliveryRequestFormDialogContextValues
  extends DeliveryRequestFormDialogContextValuesBase {
  initialOutput?: DeliveryRequestForm;
  preAlert?: PreAlertDetails;
  bookingInquiry?: AdminInquiryDetail;
}

export const DeliveryRequestFormDialogContext =
  React.createContext<DeliveryRequestFormDialogContextValues>({} as any);

export interface DeliveryRequestDeliveryInfoFormDialogContextValuesBase {
  open: boolean;
  onClose: () => void;
  deliveryRequest: DeliveryRequestListItem;
}

export interface DeliveryRequestDeliveryInfoFormDialogContextValues
  extends DeliveryRequestDeliveryInfoFormDialogContextValuesBase {}

export const DeliveryRequestDeliveryInfoFormDialogContext =
  React.createContext<DeliveryRequestDeliveryInfoFormDialogContextValues>({} as any);

export interface PaymentSlipFormDialogContextValuesBase {
  open: boolean;
  onClose: () => void;
  deliveryRequest?: DeliveryRequestListItem;
  isArrivalNoticeTag?: boolean;
  paymentSlip?: PaymentSlip;
  preAlert: PreAlertDetails;
  bookingInquiry: AdminInquiryDetail;
  type: 'create' | 'update';
}

export interface PaymentSlipFormDialogContextValues extends PaymentSlipFormDialogContextValuesBase {
  step: number;
  setStep: (step: number) => void;
  payees: Payee[];
  currencyRates: CurrencyRate[];
  paymentSlipDetailsMeta: PaymentSlipDetailsMeta;
  customsRequest?: CustomsRequest;
  paymentSlipInfoForm?: PaymentSlipInfoForm;
  setPaymentSlipInfoForm: (v: PaymentSlipInfoForm) => void;
  paymentSlipDetailsForm?: PaymentSlipDetailsForm;
  setPaymentSlipDetailsForm: (v: PaymentSlipDetailsForm) => void;
  previewPdfUrl?: string;
  setPreviewPdfUrl: (v: string) => void;
  invoiceDetails: InvoiceDetails[];
}

export const PaymentSlipFormDialogContext = React.createContext<PaymentSlipFormDialogContextValues>(
  {} as any,
);
